import React, { useEffect, useState } from "react";
import {
    Box, Grid, Typography, Select, MenuItem, Checkbox,
    Button, Snackbar, Dialog, DialogTitle, DialogContent,
    DialogActions, Alert, IconButton, TextareaAutosize, TextField,
    Backdrop, CircularProgress, Collapse, Card, CardContent
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FilterListIcon from "@mui/icons-material/FilterList";
//import axios from "axios";
import api from "../axios/axiosInstance";

import useFilteredStudents from "../hooks/useFilteredStudents";
import useSubjectsAndUnits from "../hooks/useSubjectsAndUnits";
import {
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper
} from "@mui/material";
import { Chip } from "@mui/material";
import AIGenerationLimitDialog from "../dialogs/AIGenerationLimitDialog";
import handleAIError from "../utils/handleAIError";
import { SpinningGradelyLoader, FloatingGradelyLoader } from "../loadingIcons/LoadingIcons";



const SmartSubjectComment = ({ setActiveTabGlobal }) => {
    // Filters
    const [classLevel, setClassLevel] = useState("");
    const [sectionLevel, setSectionLevel] = useState("");
    const [academicYear, setAcademicYear] = useState("");
    const [filtersExpanded, setFiltersExpanded] = useState(false);

    const [selectedStudent, setSelectedStudent] = useState("");
    const [selectedSubject, setSelectedSubject] = useState("");
    const [termNumber, setTermNumber] = useState("");
    const [unitList, setUnitList] = useState([]);
    const [includedUnits, setIncludedUnits] = useState({});
    const [subjectComment, setSubjectComment] = useState("");
    const [excludedUnits, setExcludedUnits] = useState([]);
    const [overallAssessment, setOverallAssessment] = useState("");
    const [customPrompt, setCustomPrompt] = useState("");
    const [isDirty, setIsDirty] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMsg, setAlertMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [copyAlert, setCopyAlert] = useState(false);
    const [showDiscardDialog, setShowDiscardDialog] = useState(false);
    const [pendingStudentChange, setPendingStudentChange] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const students = useFilteredStudents(classLevel, sectionLevel, academicYear);
    const { subjects } = useSubjectsAndUnits(selectedSubject);

    // Helpers to list class/section options
    const getUniqueClassLevels = () => {
        const levels = [...new Set(students.map(s => s.class_level))].filter(Boolean);
        return levels.sort();
    };
    const getUniqueSections = () => {
        const sections = [...new Set(students.map(s => s.section_level))].filter(Boolean);
        return sections.sort();
    };

    //AI Generation LIMIT
    const [showLimitDialog, setShowLimitDialog] = useState(false);


    const getAcademicYear = () => {
        const student = students.find(s => s.id === selectedStudent);
        return student?.academic_year || "";
    };

    // Reset selected student when filters change
    useEffect(() => {
        setSelectedStudent("");
    }, [classLevel, sectionLevel, academicYear]);

    useEffect(() => {
        const allValid = selectedStudent && selectedSubject && getAcademicYear() && termNumber;
        if (allValid) fetchUnitsAndComment();
        else {
            setUnitList([]);
            setIncludedUnits({});
            setSubjectComment("");
        }
    }, [selectedStudent, selectedSubject, termNumber]);

    const fetchUnitsAndComment = async (studentId = selectedStudent, subjectId = selectedSubject) => {

        const academic_year = getAcademicYear();
        try {
            const res = await api.get(`${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/check-dok-report/`, {
                params: { student: studentId, subject: subjectId, academic_year, term_number: termNumber },
                withCredentials: true
            });
            const data = res.data;
            setUnitList(data.units);
            const defaultIncluded = {};
            data.units.forEach(u => defaultIncluded[u.id] = true);
            setIncludedUnits(defaultIncluded);
        } catch {
            setUnitList([]);
        }

        setExcludedUnits([]);

        try {
            const res = await api.get(`${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/get-subject-summary/`, {
                params: { student: studentId, subject: subjectId, academic_year, term_number: termNumber },
                withCredentials: true
            });
            setSubjectComment(res.data?.summary || "");
            setOverallAssessment(res.data?.overall_assessment || "");
        } catch {
            setSubjectComment("");
            setOverallAssessment("");
        }
    };

    const handleGenerate = async () => {
        const academic_year = getAcademicYear();
        if (!selectedStudent || !selectedSubject || !overallAssessment || !academic_year || !termNumber) {
            setErrorMsg("Please select all required fields.");
            return;
        }

        const selectedUnitIds = Object.keys(includedUnits).filter(id => includedUnits[id]);
        if (selectedUnitIds.length === 0) {
            setErrorMsg("Please select at least one unit to include.");
            return;
        }


        setIsLoading(true);
        const startTime = Date.now(); // Start timer
        try {
            // 👇 Artificial delay before request
            await new Promise(resolve => setTimeout(resolve, 0)); // 5 second delay
            const res = await api.post(`${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/generate-subject-smart/`, {
                student: selectedStudent,
                subject: selectedSubject,
                overall_assessment: overallAssessment,
                pronoun: "they",
                selected_unit_ids: selectedUnitIds,
                custom_prompt: customPrompt.trim(),
                term_number: termNumber,
            }, {
                withCredentials: true
            });

            const endTime = Date.now(); // 🕒 End timer
            const duration = ((endTime - startTime) / 1000).toFixed(2); // seconds
            console.log(`✅ AI summary generated in ${duration} seconds`);
            setSubjectComment(res.data.summary);
            setExcludedUnits(res.data.excluded_units || []);
            setIsDirty(true);
            setErrorMsg("");
        } catch (err) {
            //setErrorMsg(err?.response?.data?.error || "Failed to generate subject comment.");
            // ✅ Check if it's a limit-related error

            setExcludedUnits([]);
            handleAIError(err, {
                setErrorMsg,
                setShowLimitDialog
            });
        } finally {
            setIsLoading(false);
        }
    };


    const handleSave = async () => {
        const academic_year = getAcademicYear();
        if (!subjectComment.trim()) {
            setErrorMsg("Subject comment cannot be empty.");
            return;
        }


        setIsLoading(true);
        try {
            await api.post(`${process.env.REACT_APP_BACKEND_URL}/grades/save-subject-summary/`, {
                student: selectedStudent,
                subject: selectedSubject,
                academic_year: academic_year,
                summary: subjectComment,
                overall_assessment: overallAssessment,
                term_number: termNumber,
            }, {
                withCredentials: true
            });
            setAlertMsg("✅ Subject comment saved!");
            setAlertOpen(true);
            setIsDirty(false);
            setErrorMsg("");
        } catch {
            setAlertMsg("❌ Save failed.");
            setAlertOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStudentChange = (direction) => {
        const idx = students.findIndex(s => s.id === selectedStudent);
        const newIndex = direction === "next" ? idx + 1 : idx - 1;
        const newStudent = students[newIndex];
        if (!newStudent) return;
        if (isDirty) {
            setPendingStudentChange(newStudent.id);
            setShowDiscardDialog(true);
        } else {
            setSelectedStudent(newStudent.id);
        }
    };

    const confirmDiscard = () => {
        setSelectedStudent(pendingStudentChange);
        setIsDirty(false);
        setShowDiscardDialog(false);
        setTimeout(() => {
            fetchUnitsAndComment(pendingStudentChange, selectedSubject);
        }, 0);
    };

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(subjectComment);
        setCopyAlert(true);
    };

    const getWordCount = () => {
        if (!subjectComment.trim()) return 0;
        return subjectComment.trim().split(/\s+/).length;
    };

    return (
        <Box p={3}>
            {/* Loading State */}
            {isLoading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: 400,
                            mx: 2,
                            boxShadow: 8
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Generating intelligent subject comment for student progress..."
                        />
                    </Box>
                </Box>
            )}

            {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

            <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                    {/* Collapsible Filters Section */}
                    <Paper
                        elevation={2}
                        sx={{
                            mb: 3,
                            borderRadius: 2,
                            border: filtersExpanded ? '2px solid #4caf50' : '1px solid #e0e0e0',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <Box
                            onClick={() => setFiltersExpanded(!filtersExpanded)}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                backgroundColor: filtersExpanded ? '#f1f8e9' : 'transparent',
                                borderRadius: filtersExpanded ? '8px 8px 0 0' : '8px',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FilterListIcon
                                    sx={{
                                        color: filtersExpanded ? '#4caf50' : '#666',
                                        fontSize: 20
                                    }}
                                />
                                <Typography
                                    fontWeight="bold"
                                    sx={{
                                        color: filtersExpanded ? '#4caf50' : 'text.primary',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    Filter Students
                                </Typography>
                                {(classLevel || sectionLevel || academicYear) && (
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: '#ff5722',
                                            ml: 1
                                        }}
                                    />
                                )}
                            </Box>
                            {filtersExpanded ?
                                <ExpandLessIcon sx={{ color: '#4caf50' }} /> :
                                <ExpandMoreIcon sx={{ color: '#666' }} />
                            }
                        </Box>

                        <Collapse in={filtersExpanded}>
                            <Box sx={{ p: 2, pt: 0 }}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        fontWeight="600"
                                        sx={{ mb: 1, fontSize: '0.875rem', color: '#333' }}
                                    >
                                        Class Level
                                    </Typography>
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={classLevel}
                                        onChange={(e) => setClassLevel(e.target.value)}
                                        displayEmpty
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                backgroundColor: 'white'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">All Classes</MenuItem>
                                        {getUniqueClassLevels().map(level => (
                                            <MenuItem key={level} value={level}>{level}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        fontWeight="600"
                                        sx={{ mb: 1, fontSize: '0.875rem', color: '#333' }}
                                    >
                                        Section
                                    </Typography>
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={sectionLevel}
                                        onChange={(e) => setSectionLevel(e.target.value)}
                                        displayEmpty
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                backgroundColor: 'white'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">All Sections</MenuItem>
                                        {getUniqueSections().map(section => (
                                            <MenuItem key={section} value={section}>{section}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        fontWeight="600"
                                        sx={{ mb: 1, fontSize: '0.875rem', color: '#333' }}
                                    >
                                        Academic Year
                                    </Typography>
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={academicYear}
                                        onChange={(e) => setAcademicYear(e.target.value)}
                                        displayEmpty
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                backgroundColor: 'white'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">All Years</MenuItem>
                                        {[
                                            "2025-2026",
                                            "2026-2027",
                                            "2027-2028",
                                            "2028-2029",
                                            "2029-2030",
                                        ].map((year) => (
                                            <MenuItem key={year} value={year}>{year}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>

                                {/* Clear Filters Button */}
                                {(classLevel || sectionLevel || academicYear) && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        onClick={() => {
                                            setClassLevel("");
                                            setSectionLevel("");
                                            setAcademicYear("");
                                        }}
                                        sx={{
                                            mt: 1,
                                            textTransform: 'none',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        Clear All Filters
                                    </Button>
                                )}
                            </Box>
                        </Collapse>
                    </Paper>

                    <Typography fontWeight="bold">Select Student</Typography>
                    <Select fullWidth value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)}>
                        <MenuItem value="">Select Student</MenuItem>
                        {students.map(s => (
                            <MenuItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.class_level} - {s.section_level})</MenuItem>
                        ))}
                    </Select>

                    <Typography variant="body2" color="textSecondary" mt={1}>
                        {students.length} students available
                    </Typography>

                    <Typography fontWeight="bold" mt={2}>Select Subject</Typography>
                    <Select fullWidth value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
                        <MenuItem value="">Select Subject</MenuItem>
                        {subjects.map(sub => (
                            <MenuItem key={sub.id} value={sub.id}>{`${sub.subject_name} ${sub.class_level}`}</MenuItem>
                        ))}
                    </Select>

                    <Typography fontWeight="bold" mt={2}>Select Term</Typography>
                    <Select fullWidth value={termNumber} onChange={e => setTermNumber(e.target.value)}>
                        <MenuItem value="">Select Term</MenuItem>
                        <MenuItem value={1}>Term 1</MenuItem>
                        <MenuItem value={2}>Term 2</MenuItem>
                        <MenuItem value={3}>Term 3</MenuItem>
                    </Select>

                    <Box mt={2} display="flex" gap={1}>
                        <Button variant="outlined" disabled={students.findIndex(s => s.id === selectedStudent) === 0}
                            onClick={() => handleStudentChange("prev")}>{"< Prev"}</Button>
                        <Button variant="outlined" disabled={students.findIndex(s => s.id === selectedStudent) === students.length - 1}
                            onClick={() => handleStudentChange("next")}>{"Next >"}</Button>
                    </Box>
                </Grid>

                <Grid item xs={12} md={9}>
                    <Typography variant="h6" fontWeight="bold" mb={1}>
                        Units
                    </Typography>

                    <TableContainer component={Paper} elevation={1} sx={{ mb: 3 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Unit Name</strong></TableCell>
                                    <TableCell><strong>DOK Report</strong></TableCell>
                                    <TableCell><strong>Overall Assessment</strong></TableCell>
                                    <TableCell><strong>Include in Comment</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {unitList.map(unit => (
                                    <TableRow key={unit.id}>
                                        <TableCell>{unit.unit_name}</TableCell>
                                        <TableCell>
                                            {unit.dok_exists ? (
                                                <Typography color="success.main">✅ DOK Report Exists</Typography>
                                            ) : (
                                                <Typography color="error.main">❌ No DOK Report</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {unit.dok_exists ? (
                                                <Chip
                                                    label={unit.overall_assessment}
                                                    color={
                                                        unit.overall_assessment === "Extending" ? "success" :
                                                            unit.overall_assessment === "Proficient" ? "primary" :
                                                                unit.overall_assessment === "Developing" ? "warning" :
                                                                    unit.overall_assessment === "Emerging" ? "error" : "default"
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            ) : (
                                                <Typography color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Checkbox
                                                checked={includedUnits[unit.id] || false}
                                                onChange={(e) => setIncludedUnits({
                                                    ...includedUnits,
                                                    [unit.id]: e.target.checked
                                                })}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {excludedUnits.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
                                {excludedUnits.length === 1 ? "1 unit was" : `${excludedUnits.length} units were`} left out of this comment:
                            </Typography>
                            {excludedUnits.map((u, idx) => (
                                <Typography key={idx} variant="body2">
                                    • {u.unit_name} — {u.reason}
                                </Typography>
                            ))}
                        </Alert>
                    )}

                    <Box display="flex" alignItems="center" mt={4}>
                        <Typography fontWeight="bold" mr={1}>Subject Comment</Typography>
                        <IconButton onClick={handleCopyToClipboard}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <TextareaAutosize
                        minRows={6}
                        value={subjectComment}
                        onChange={(e) => {
                            setSubjectComment(e.target.value);
                            setIsDirty(true);
                        }}
                        style={{
                            width: '100%',
                            resize: 'none',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fff',
                            padding: '10px',
                            fontFamily: 'inherit',
                            fontSize: '16px',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                    />
                    <Typography variant="caption" color="textSecondary" mt={1} display="block">
                        Word Count: {getWordCount()}
                    </Typography>

                    <Typography fontWeight="bold" mt={2}>Overall Assessment</Typography>
                    <Select fullWidth value={overallAssessment} onChange={e => setOverallAssessment(e.target.value)}>
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="Extending">Extending</MenuItem>
                        <MenuItem value="Proficient">Proficient</MenuItem>
                        <MenuItem value="Developing">Developing</MenuItem>
                        <MenuItem value="Emerging">Emerging</MenuItem>
                    </Select>

                    <Typography fontWeight="bold" mt={2}>Additional Instructions</Typography>
                    <TextField
                        fullWidth multiline minRows={3} maxRows={6}
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="Optional teacher guidance for AI (e.g., 'Include feedback about class participation')"
                    />

                    <Box display="flex" gap={2} mt={2}>
                        <Button variant="contained" color="primary" onClick={handleGenerate}>Generate Comment</Button>
                        <Button variant="contained" color="success" onClick={handleSave}>Save Comment</Button>
                        {isDirty && <Typography color="warning.main" fontWeight="bold">⚠️ Unsaved changes</Typography>}
                    </Box>
                </Grid>
            </Grid>

            <Snackbar open={alertOpen} autoHideDuration={3000} onClose={() => setAlertOpen(false)} message={alertMsg} />
            <Snackbar open={copyAlert} autoHideDuration={2000} onClose={() => setCopyAlert(false)} message="📋 Subject Comment Copied" />

            <Dialog open={showDiscardDialog} onClose={() => setShowDiscardDialog(false)}>
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                    <Typography>You have unsaved changes. Discard and continue?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDiscardDialog(false)}>Cancel</Button>
                    <Button onClick={confirmDiscard} color="error">Discard</Button>
                </DialogActions>
            </Dialog>
            <AIGenerationLimitDialog
                open={showLimitDialog}
                onClose={() => setShowLimitDialog(false)}
                featureName="Subject Summary Generation"
                goToUpgrade={() => setActiveTabGlobal("Upgrade")} // 💡 this switches the tab
            />
        </Box>
    );
};

export default SmartSubjectComment;
